import { useState } from "react";
import { DialogFooter } from "../../ui/dialog";
import StaffingModal from "./StaffingModal";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Alert, AlertDescription, AlertTitle } from "../../ui/alert";
import { AlertCircle } from "lucide-react";
import { SERVICE_TYPES, ServiceType } from "@/types/staffing/posting";
import { createPosting } from "@/services/staffing/Staffing";
import { useApiClient } from "@/context/ApiContext";
import { useStaffing } from "@/context/StaffingContext";
import { getServiceTypeFilterFromView, getViewFromServiceType } from "@/utils/staffingUtil";

interface NewPostingModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    clientId: number;
    clientName: string | null;
}

interface ApiError {
    title: string;
    detail: string;
}

const NewPostingModal = ({ open, onOpenChange, clientId, clientName }: NewPostingModalProps) => {
    const [serviceType, setServiceType] = useState<ServiceType | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ApiError | null>(null);

    const apiClient = useApiClient();
    const { currentRegion, fetchPostings } = useStaffing();
    const handleSubmit = () => {
        if (!serviceType) return;
        setLoading(true);
        setError(null);
        createPosting({ clientId, serviceType, telehealth: false }, apiClient)
            .then(() => onOpenChange(false))
            .catch(err => {
                if (err?.response?.status === 409) {
                    const data = err.response.data;
                    setError({ title: data?.title ?? 'Conflict', detail: data?.detail ?? 'A duplicate posting already exists.' });
                } else {
                    console.error(err);
                }
            })
            .finally(() => {
                let view = getViewFromServiceType(serviceType)
                fetchPostings(
                    view,
                    {
                        filter: `region eq '${currentRegion}' ${getServiceTypeFilterFromView(view)}`,
                        expand: ['processes,client'],
                        orderby: 'id desc'
                    })
                setLoading(false)
            });
    };

    const handleOpenChange = (val: boolean) => {
        if (!val) {
            setServiceType(null);
            setError(null);
        }
        onOpenChange(val);
    };

    return (
        <StaffingModal open={open} onOpenChange={handleOpenChange} title={`New Posting for ${clientName}`}>
            <div className="flex flex-col gap-3 p-2 max-w-[60vw] m-2">
                <Label htmlFor="service-type">Service Type</Label>
                <Select value={serviceType ?? ''} onValueChange={(val) => setServiceType(val as ServiceType)}>
                    <SelectTrigger id="service-type">
                        <SelectValue placeholder="Select service type..." />
                    </SelectTrigger>
                    <SelectContent>
                        {SERVICE_TYPES.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{error.title}</AlertTitle>
                        <AlertDescription>{error.detail}</AlertDescription>
                    </Alert>
                )}
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={!serviceType || loading}>
                    {loading ? 'Posting...' : 'Post it!'}
                </Button>
            </DialogFooter>
        </StaffingModal>
    );
};

export default NewPostingModal;
